import { formatAcapsPopulation } from './acapsPopulation';

export interface QuantitativeIndicatorRow {
    label: string;
    value: string;
    period?: string;
}

export interface QuantitativeIndicatorGroup {
    title: string;
    rows: QuantitativeIndicatorRow[];
}

const number = (value: unknown): number | null => {
    const parsed = Number(value);
    return value !== null && value !== '' && Number.isFinite(parsed) ? parsed : null;
};

const decimal = (value: number, digits = 1) => new Intl.NumberFormat('en-US', {
    maximumFractionDigits: digits,
}).format(value);

const compact = (value: number) => new Intl.NumberFormat('en-US', {
    notation: 'compact', maximumFractionDigits: 1,
}).format(value);

const money = (value: number) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1,
}).format(value);

function add(rows: QuantitativeIndicatorRow[], label: string, raw: unknown, format: (value: number) => string, period?: unknown) {
    const value = number(raw);
    if (value !== null)
        rows.push({ label, value: format(value), ...(period ? { period: String(period) } : {}) });
}

export function quantitativeIndicatorGroups(indicators: any): QuantitativeIndicatorGroup[] {
    if (!indicators || typeof indicators !== 'object')
        return [];
    const groups: QuantitativeIndicatorGroup[] = [];
    const humanitarian = indicators.acaps;
    if (humanitarian && typeof humanitarian === 'object') {
        const rows: QuantitativeIndicatorRow[] = [];
        add(rows, 'Active crises', humanitarian.crisisCount, value => decimal(value, 0));
        if (rows.length || 'totalAffected' in humanitarian)
            rows.push({ label: 'Population affected', value: formatAcapsPopulation(humanitarian.totalAffected, true) });
        if (rows.length)
            groups.push({ title: 'Humanitarian', rows });
    }
    const economic = indicators.worldBank;
    if (economic && typeof economic === 'object') {
        const rows: QuantitativeIndicatorRow[] = [];
        const year = (key: string) => economic.indicatorYears?.[key] ?? economic.dataYear;
        add(rows, 'GDP growth', economic.gdpGrowth, value => `${decimal(value)}%`, year('gdpGrowth'));
        add(rows, 'GDP', economic.gdpAbsolute, money, year('gdpAbsolute'));
        add(rows, 'Inflation', economic.inflation, value => `${decimal(value)}%`, year('inflation'));
        add(rows, 'Unemployment', economic.unemployment, value => `${decimal(value)}%`, year('unemployment'));
        add(rows, 'GNI per capita', economic.gni_per_capita, money, year('gni_per_capita'));
        add(rows, 'Government debt to GDP', economic.debt_to_gdp, value => `${decimal(value)}%`, year('debt_to_gdp'));
        add(rows, 'Foreign reserves', economic.foreignReserves, money, year('foreignReserves'));
        add(rows, 'Trade to GDP', economic.trade_to_gdp, value => `${decimal(value)}%`, year('trade_to_gdp'));
        add(rows, 'Population', economic.population, compact, year('population'));
        add(rows, 'Poverty rate', economic.poverty, value => `${decimal(value)}%`, year('poverty'));
        if (rows.length)
            groups.push({ title: 'Economic', rows });
    }
    const health = indicators.who;
    if (health && typeof health === 'object') {
        const rows: QuantitativeIndicatorRow[] = [];
        const lifeExpectancy = health.lifeExpectancy;
        const coverage = health.universalHealthCoverage;
        add(rows, 'Life expectancy', lifeExpectancy?.value ?? lifeExpectancy, value => `${decimal(value)} years`, lifeExpectancy?.year);
        add(rows, 'Universal health coverage index', coverage?.value ?? coverage, value => decimal(value), coverage?.year);
        if (rows.length)
            groups.push({ title: 'Health', rows });
    }
    return groups;
}
